import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import scaylorOrb from '../assets/images/ScaylorOrbB.svg';
import curvedWisp from '../assets/images/Background Curve Wisp.png';
import bluePinkGradient from '../assets/images/bluePnkGradient.svg';
import pinkRedGradient from '../assets/images/pinkRedGradient.svg';

function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [inviteDetails, setInviteDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const verifyInvite = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/invites/verify/${token}`);
        
        if (response.ok) {
          const data = await response.json();
          setInviteDetails(data);
          setEmail(data.email);
        } else {
          const errorData = await response.json();
          setError(errorData.message || 'Invalid or expired invitation');
        }
      } catch (err) {
        setError('Failed to verify invitation');
      } finally {
        setIsLoading(false);
      }
    };

    verifyInvite();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/invites/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          email,
          password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        navigate('/');
      } else {
        setError(data.message || 'An error occurred');
      }
    } catch (err) {
      setError('Failed to accept invitation');
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <img
          src={scaylorOrb}
          alt="Loading..."
          className="w-24 h-24 animate-spin"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-md rounded-3xl p-12 text-center">
          <h2 className="text-2xl font-bold mb-4">Invalid Invitation</h2>
          <p className="text-red-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex items-start justify-center pt-24 overflow-hidden relative">
      {/* Background Wisp */}
      <div className="absolute bottom-0 left-0">
        <img 
          src={bluePinkGradient} 
          alt="Blue Pink Gradient" 
          className="w-[800px] opacity-500"
        />
      </div>

      {/*bluePinkGradient*/}
      <div className="absolute top-0 left-0">
        <img 
          src={curvedWisp} 
          alt="Background Wisp" 
          className="w-[300px] opacity-100"
        />
      </div>

      {/*pinkRedGradient*/}
      <div className="absolute top-0 right-0">
        <img 
          src={pinkRedGradient} 
          alt="Pink Red Gradient" 
          className="w-[700px] opacity-100"
        />
      </div>

      {/* Orb Background */}
      <div className="absolute inset-0 flex items-start justify-center pt-[4%]">
        <img 
          src={scaylorOrb} 
          alt="Scaylor Orb" 
          className="w-[800px] opacity-50"
        />
      </div>

      {/* Accept Invite Form - Glass Effect */}
      <div className="bg-white/10 backdrop-blur-md rounded-3xl p-44 py-[125px] w-[650px] shadow-lg z-10 relative mt-[5%]">
        <div className="flex flex-col items-center">
          <h1 className="text-7xl font-bold font-montserrat text-shadow-custom -mt-24 mb-6">SCAYLOR</h1>
        </div>
        
        <h2 className="text-2xl mb-2 font-Gilroy font-bold">Accept Invitation</h2>
        
        {inviteDetails && (
          <p className="text-gray-600 mb-6 text-center">
            {inviteDetails.inviter.name} has invited you to join{' '}
            <span className="font-semibold">{inviteDetails.organization.name}</span>{' '}
            as a {inviteDetails.role}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm mb-2 font-Gilroy font-medium">Email</label>
            <input
              type="email"
              name="email"
              value={email}
              readOnly
              className="w-full px-3 py-2 border rounded-lg bg-gray-100 cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm mb-2 font-Gilroy font-medium">Create Password</label>
            <input
              type="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your new password"
              className="w-full px-3 py-2 border rounded-lg"
              required
            />
          </div>

          <button
            type="submit"
            className="w-full bg-black text-white py-2 rounded-lg hover:bg-gray-800 font-Gilroy font-bold mt-6"
          >
            Accept & Join Team
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <span
            onClick={() => navigate('/login')}
            className="text-blue-600 cursor-pointer hover:underline"
          >
            Sign in instead
          </span>
        </p>
      </div>
    </div>
  );
}

export default AcceptInvite; 